import { useEffect } from 'react'
import { getContrastTextColor, hexToHsl } from '@structural-climbing/ui'

/**
 * Paints the whole member app in one gym's colours.
 *
 * Two families of variable come out of this:
 *
 *  - `--heroui-*`, which re-point HeroUI's own palette. This is what makes
 *    every `color="primary"`, `bg-primary`, `text-primary` and `border-divider`
 *    already in the app gym-branded without touching a single component.
 *  - `--gym-*`, the surfaces the redesign is built on — the page wash, the
 *    inset veil, the hairline, the halo behind the hero. These replace the raw
 *    hex that used to be inlined at six separate call sites, each with its own
 *    slightly different idea of how to tint.
 *
 * The tag is written once and updated in place, never removed. That is
 * deliberate: /profile, /competitions and /our-gyms render outside GymLayout,
 * and a member who walks from their gym's dashboard into Competitions should
 * not watch the app change colour underneath them.
 */
export function useGymTheme(primaryColor: string | null, secondaryColor: string | null) {
  useEffect(() => {
    if (!primaryColor) return

    const primary = hexToHsl(primaryColor)
    const secondary = secondaryColor ? hexToHsl(secondaryColor) : null
    const onPrimary = getContrastTextColor(primaryColor)

    // HeroUI wants its foreground as HSL parts, not a hex, and the two greys
    // here are the theme's own foreground and near-black.
    const primaryForeground = onPrimary === '#ffffff' ? '60 8.57% 93.14%' : '0 0% 3.92%'

    // The wash, veil and line are the same hue held at three lightnesses, with
    // saturation pulled down as they get lighter — a flat tint at 97% lightness
    // still reads as coloured paper, while a saturated one reads as a mistake.
    // Falling back to the primary's hue keeps a gym that only set one colour
    // from landing on dead grey.
    const tintSource = secondary ?? primary
    const wash = tint(tintSource, 97, 0.45)
    const veil = tint(tintSource, 94, 0.4)
    const line = tint(tintSource, 91, 0.35)

    // A brand colour is chosen to look good as a logo, not to survive as a
    // solid mark on near-white paper: a gym whose primary is a pale sand would
    // draw chart bars nobody can see. This holds the hue and forces it into a
    // band that always reads against the page.
    const ink = readableOnLight(primary)

    let el = document.getElementById('gym-theme') as HTMLStyleElement | null
    if (!el) {
      el = document.createElement('style')
      el.id = 'gym-theme'
      document.head.appendChild(el)
    }

    el.textContent = `
      :root, [data-theme] {
        --heroui-primary: ${primary};
        --heroui-primary-foreground: ${primaryForeground};
        --heroui-focus: ${primary};
        ${secondary ? `--heroui-secondary: ${secondary};` : ''}
        --heroui-divider: ${line};

        --gym-primary: ${primaryColor};
        --gym-on-primary: ${onPrimary};
        --gym-wash: hsl(${wash});
        --gym-veil: hsl(${veil});
        --gym-line: hsl(${line});
        --gym-glow: hsl(${primary} / 0.12);
        --gym-ink: hsl(${ink});
      }
    `

    // Colour the browser chrome to match, so the status bar in the installed
    // PWA belongs to the gym instead of sitting on a hard-coded off-white.
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', hslToCss(wash))
  }, [primaryColor, secondaryColor])
}

/** Contrast of the lightest wash we ever paint — the worst case for a mark. */
const SURFACE_LUMINANCE = relativeLuminance(35, 15, 97)

/** Solid marks need 3:1 against their surface; the margin absorbs rounding. */
const MARK_CONTRAST = 3.2

/**
 * The brand hue, darkened until it actually carries on a light surface.
 *
 * The clamp is on measured contrast, not on HSL lightness, because the two come
 * apart badly at the top of the spectrum: a yellow at 50% lightness is still
 * three times brighter than a blue at 50%, and drawing chart bars in it gives
 * you 1.5:1 — a shape you can see only if you already know it is there.
 *
 * Saturation has a floor so a muted brand still reads as a colour, but only for
 * colours that have a hue at all: applying it to a grey would invent a red.
 */
function readableOnLight(hsl: string): string {
  const [rawH, rawS, rawL] = hsl.split(' ')
  const h = parseFloat(rawH)
  const s = parseFloat(rawS) < 10 ? parseFloat(rawS) : Math.max(parseFloat(rawS), 45)

  for (let l = Math.min(parseFloat(rawL), 52); l >= 12; l--) {
    if (contrastWithSurface(relativeLuminance(h, s, l)) >= MARK_CONTRAST) return `${h} ${s}% ${l}%`
  }
  return `${h} ${s}% 12%`
}

function contrastWithSurface(luminance: number): number {
  const lighter = Math.max(luminance, SURFACE_LUMINANCE)
  const darker = Math.min(luminance, SURFACE_LUMINANCE)
  return (lighter + 0.05) / (darker + 0.05)
}

function relativeLuminance(h: number, s: number, l: number): number {
  const sat = s / 100
  const lit = l / 100
  const k = (n: number) => (n + h / 30) % 12
  const a = sat * Math.min(lit, 1 - lit)
  const channel = (n: number) => lit - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  const [r, g, b] = [channel(0), channel(8), channel(4)].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4),
  )
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Holds a hue, forces a lightness, and scales saturation down with it. */
function tint(hsl: string, lightness: number, saturationFactor: number): string {
  const [h, s] = hsl.split(' ')
  return `${h} ${(parseFloat(s) * saturationFactor).toFixed(1)}% ${lightness}%`
}

/** `meta[theme-color]` predates the space-separated syntax, so it needs commas. */
function hslToCss(hsl: string): string {
  const [h, s, l] = hsl.split(' ')
  return `hsl(${h}, ${s}, ${l})`
}
